/**
 * Browser half of a contact-photo upload: validate the picked file against the
 * same limits the route enforces, shrink a large one so it fits under Vercel's
 * request-body cap, and POST it. Client-only (uses `createImageBitmap` +
 * canvas); the server re-validates and re-encodes everything regardless.
 */

/** Must match `MAX_AVATAR_INPUT_BYTES` (store.ts) — duplicated only to avoid a server import in the browser. */
export const MAX_AVATAR_UPLOAD_BYTES = 5 * 1024 * 1024;

/** Must match `ACCEPTED_AVATAR_MIME_TYPES` (store.ts). */
export const ACCEPTED_AVATAR_UPLOAD_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;

/**
 * Vercel Functions refuse request bodies over 4.5 MB, below our 5 MB file cap.
 * A file above this is downscaled in the browser first (the server shrinks it
 * to 512px anyway, so nothing visible is lost).
 */
const SEND_AS_IS_MAX_BYTES = 4 * 1024 * 1024;
const SHRINK_EDGE_PX = 2048;

export type AvatarFileProblem = "type" | "size";

/** Why a picked file cannot be uploaded, or null when it can. */
export function avatarFileProblem(file: { type: string; size: number }): AvatarFileProblem | null {
  if (!(ACCEPTED_AVATAR_UPLOAD_TYPES as readonly string[]).includes(file.type)) return "type";
  if (file.size > MAX_AVATAR_UPLOAD_BYTES) return "size";
  return null;
}

async function shrink(file: File): Promise<Blob> {
  if (file.size <= SEND_AS_IS_MAX_BYTES) return file;
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  try {
    const scale = Math.min(1, SHRINK_EDGE_PX / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
    return blob ?? file;
  } finally {
    bitmap.close();
  }
}

interface AvatarRouteBody {
  avatarUrl?: string | null;
  error?: string;
}

function isAvatarRouteBody(value: unknown): value is AvatarRouteBody {
  return typeof value === "object" && value !== null;
}

async function readBody(response: Response): Promise<AvatarRouteBody> {
  try {
    const parsed: unknown = await response.json();
    return isAvatarRouteBody(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

/** Upload (or replace) a contact's photo; resolves to the new `avatar_url`. Throws with a user-facing message. */
export async function uploadContactAvatar(contactId: string, file: File): Promise<string> {
  const body = new FormData();
  const blob = await shrink(file);
  body.append("file", blob, blob === file ? file.name : "avatar.jpg");
  const response = await fetch(`/api/contacts/${encodeURIComponent(contactId)}/avatar`, {
    method: "POST",
    body,
    credentials: "same-origin",
  });
  const parsed = await readBody(response);
  if (!response.ok || typeof parsed.avatarUrl !== "string") {
    throw new Error(parsed.error || "Could not upload that photo.");
  }
  return parsed.avatarUrl;
}

/** Remove a contact's photo (the pointer only — the stored image is kept). */
export async function removeContactAvatar(contactId: string): Promise<void> {
  const response = await fetch(`/api/contacts/${encodeURIComponent(contactId)}/avatar`, {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!response.ok) {
    const parsed = await readBody(response);
    throw new Error(parsed.error || "Could not remove that photo.");
  }
}
